import { Layer, Publication } from './catalog';
import { InterpolationService } from '../interpolation.service';
import { EventEmitter } from '@angular/core';
import { ScaledStyle } from './vector-styles';
import { Feature, FeatureCollection } from 'geojson';
import { containsFilterTextNested } from '../tree-filter.service';
import { TimeUtilsService } from '../time-utils.service';

export type MappedLayerTypes = 'wms' | 'vector' | 'circle';

export interface MappedLayerOptions {
  legend?: boolean;
  publication?: number;
  date?: Date,

  [key: string]: any;
}

const PUBLICATION_PRIORITY_ORDER = [
  'annual',
  'monthly',
  'daily'
];

const MAKE_DOWNLOAD_URL:{[key:string]:(a:string,s:string,ml:MappedLayer)=>string} = {
  tds:(host:string,fn:string)=>{
    let components = fn.split('/');
    components.pop();
    return `${host}/catalog/${components.join('/')}/catalog.html`;
  },
  static:(host:string,fn:string,ml:MappedLayer)=>{
    return ml.layer.options.downloadPath || `${host}${fn}`;
  }
}

const DEFAULT_VECTOR_STYLES: {[key:string]:any} = {
  zonal: {
    color: '#000000',
    weight: 1
  }
}

export const WMS_PARAMETER_NAMES:{[key:string]:Array<string>} = {
  tds: [
    'layers',
    'styles',
    'colorscalerange',
    'abovemaxcolor',
    'belowmincolor',
    'time',
    'transparent',
    'logscale'
  ],
  geoserver: [
    'transparent',
    'layers'
  ],
  esri: [
    'layers',
    'styles',
    'transparent'
  ],
  wms: [
    'layers',
    'styles',
    'transparent',
    'time'
  ],
  none: []
};

export const WMS_URL_FORMAT:{[key:string]:string} = {
  tds:'/wms/',
  geoserver:'/wms/',
  esri:'/',
  wms:'',
  static:''
};

export const INTERPOLATED_PARAMETERS = [
  'styles',
  'layers'
];

export const IMPLICIT_STYLE_PARMETERS:{[key:string]:string} = {
  Colour_Hex: 'fillColor'
};

export class MappedLayer {
  constructor(data?:any){
    Object.assign(this,data||{});
    if(this.layerType===undefined){
      this.layerType = this.wmsParameters?'wms':undefined;
    }
  }

  title:string;

  layer: Layer;
  options: MappedLayerOptions = {
    date: new Date(Date.UTC(2016, 0, 1)) // Set to most recent available date
  };

  legendURL:string;
  layerType: MappedLayerTypes;
  retrievedMetadata: {[key:string]:any} = {};

  interpolatedFile:string;
  interpolatedDownloadURL:string;
  url: string;
  wmsParameters: any = {};
  flattenedSettings: any = {};

  staticDataChanged = new EventEmitter<FeatureCollection>();
  private _fullStaticData:FeatureCollection;
  private _filteredStaticData:FeatureCollection;
  get staticData():FeatureCollection {
    return this._filteredStaticData;
  }
  set staticData(data:FeatureCollection){
    this._fullStaticData = data;
    this.filterVectors();
    this.staticDataChanged.emit(data);
  }

  filterVectors():void{
    const publication = this.layer.publications[this.options.publication];
    this._filteredStaticData = filterFeatures(this._fullStaticData,publication.options.filter?.text);
  }

  opacity = 1.0;

  _styleFunc: (f:any)=>void;

  description():string{
    return this.layer.description ||
      (this.retrievedMetadata &&
       this.retrievedMetadata[this.layer.descriptionField||'long_name']);
  }

  leading0(n: number): string {
    if (n < 10) {
      return '0' + n;
    }
    return '' + n;
  }

  defaultPublication():number{
    const priorityPublication = PUBLICATION_PRIORITY_ORDER.find(
      pp=>this.layer.publications.findIndex(lp=>(lp.timestep===pp)||(lp.label===pp))>=0);
    if(priorityPublication){
      return this.layer.publications.findIndex(p=>(p.label===priorityPublication)||(p.timestep===priorityPublication));
    }
    return this.layer.publications.findIndex(p => !p.skip);
  }

  initialiseDefaults() {
    const pub = this.layer.publications[this.options.publication];
    console.log(pub);
    if(pub.options.end){
      this.options.date = TimeUtilsService.parseDate(pub.options.end) as Date;
    }
  }

  update() {
    if(!this.layer.publications?.length){
      this.layer.publications.push({
        options:this.layer.options||{}
      } as Publication);
    }
    this.options.publication = (this.options.publication === undefined) ?
      this.defaultPublication() :
      this.options.publication;

    const publication = this.layer.publications[this.options.publication];
    const options = publication.options;
    const host = options.host || {};
    const baseURL = host.url;

    const software = host.software || 'tds';
    const oldFile = this.interpolatedFile;
    this.interpolatedFile = options.filepath || options.datapath || '';
    const mapParams = Object.assign({},
      this.layer,
      publication.options,
      publication.options.mapOptions || {},
      this.options.date ? {
        decade: decadeText(this.options.date),
        year: this.options.date.getFullYear(),
        month: this.leading0(this.options.date.getMonth() + 1),
        day: this.leading0(this.options.date.getDate()),
      } : {},
      this.options,
      this.options.tags || {});

    if (mapParams.timeFormat) {
      mapParams['time'] = InterpolationService.interpolate(mapParams.timeFormat, mapParams);
    }
    mapParams.layers = mapParams.layers || mapParams.layer || mapParams.variable;
    INTERPOLATED_PARAMETERS.forEach(p=>{
      if(mapParams[p]){
        mapParams[p] = InterpolationService.interpolate(mapParams[p],mapParams);
      }
    });
    this.interpolatedFile = InterpolationService.interpolate(this.interpolatedFile, mapParams);
    this.url = baseURL + WMS_URL_FORMAT[software] + this.interpolatedFile;
    if(MAKE_DOWNLOAD_URL[software]){
      this.interpolatedDownloadURL=MAKE_DOWNLOAD_URL[software](host.downloadLink||baseURL,this.interpolatedFile,this);
    } else {
      this.interpolatedDownloadURL=host.downloadLink||this.layer?.options?.downloadPath||null;
    }

    if((this.layer.options.legend==='wms')||(publication.options.legend==='wms')){
      this.legendURL = this.url + '?service=WMS&request=GetLegendGraphic&format=image/png';
      this.legendURL += `&layer=${InterpolationService.interpolate(mapParams.layers,mapParams)}`;
      this.legendURL += '&version=1.1.1';
      this.options.legend=true;
    } else {
      this.legendURL=null;
    }

    if (mapParams.vectors) {
      this.wmsParameters = null;
      this.updatedVectorLayer(oldFile, mapParams);
    } else {
      this.updatedWMSLayer(software, mapParams);
    }
    this.flattenedSettings = mapParams;

    if(mapParams.titleFormat){
      this.title = InterpolationService.interpolate(mapParams.titleFormat,mapParams)
    } else {
      this.title = this.layer.name;
    }
  }

  private updatedWMSLayer(software: string, mapParams: any) {
    this.layerType = 'wms';
    this.wmsParameters = {};
    WMS_PARAMETER_NAMES[software].forEach(param => {
      if (mapParams[param]) {
        this.wmsParameters[param] = mapParams[param];
      }
    });
    if(software==='wms'){
      Object.keys(mapParams?.tags||{}).forEach(k=>{
        if(this.wmsParameters[k]){
          return;
        }
        this.wmsParameters[`DIM_${k}`]=mapParams.tags[k]
      });
    }
  }

  private updatedVectorLayer(oldFile: string, mapParams: any) {
    if (this.interpolatedFile !== oldFile) {
      this.staticData = null;
    }

    if (this.staticData) {
      this.filterVectors();
    }

    this.layerType = 'vector';
    const styles = (typeof (mapParams.styles) == 'string') ? {} : (mapParams.styles || {});
    const customStyles = (typeof (mapParams.customStyles) == 'string') ? {} : (mapParams.customStyles || {});
    this._styleFunc = (f: any) => {
      let result: any = {};
      Object.keys(IMPLICIT_STYLE_PARMETERS).forEach(k => {
        if (f?.properties?.[k]) {
          result[IMPLICIT_STYLE_PARMETERS[k]] = f.properties[k];
        }
      });
      result = { ...DEFAULT_VECTOR_STYLES[mapParams.vectors], ...result, ...styles, ...customStyles };
      const keys = Object.keys(result);
      keys.forEach(k => {
        if ((result[k] as ScaledStyle<any>).getStyleValue) {
          result[k] = (result[k] as ScaledStyle<any>).getStyleValue(f);
        }
      });
      if (!result.fillColor) {
        result.fillOpacity = 0;
      }
      return result;
    };

    if (mapParams.vectors === 'point' && mapParams.styles) {
      this.layerType = 'circle';
    }
  }

  reset() {
    const publication = this.layer.publications[this.options.publication];
    publication.options.customLegend = null;
    publication.options.customStyles = null;
    publication.options.filter = {
      text:null
    };
  }
}

function decadeText(d: Date): string {
  let decade = d.getFullYear().toString().slice(0, 3);
  return `${decade}0-${decade}9`;
}

function filterFeatures(features: FeatureCollection, filterText: string): FeatureCollection {
  if(!features){
    return features;
  }

  if(!filterText){
    return features;
  }

  let result:FeatureCollection = {
    type: 'FeatureCollection',
    features: [] as Feature[]
  };

  result.features = features.features.filter(f=>containsFilterTextNested(f.properties,filterText));
  return result;
}

